/**
 * Storage Formatter Utility
 *
 * Provides consistent storage formatting across the application.
 * Always uses bytes as the source of truth and converts to human-readable format.
 */

/**
 * Format storage bytes to GB with consistent precision
 * @param bytes Storage size in bytes
 * @returns Formatted string with GB suffix (e.g., "1.40 GB")
 */
export function formatStorageGB(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null || bytes === 0) {
    return '0 GB';
  }

  const gb = bytes / (1024 * 1024 * 1024);
  return `${gb.toFixed(2)} GB`;
}

/**
 * Format storage bytes to MB with consistent precision
 * @param bytes Storage size in bytes
 * @returns Formatted string with MB suffix (e.g., "1430.51 MB")
 */
export function formatStorageMB(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null || bytes === 0) {
    return '0 MB';
  }

  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
}

/**
 * Format storage bytes to human-readable format (auto-select unit)
 * @param bytes Storage size in bytes
 * @returns Formatted string with appropriate suffix (e.g., "1.40 GB" or "512 MB")
 */
export function formatStorageAuto(bytes: number | undefined | null): string {
  if (bytes === undefined || bytes === null || bytes === 0) {
    return '0 B';
  }

  const kb = bytes / 1024;
  const mb = bytes / (1024 * 1024);
  const gb = bytes / (1024 * 1024 * 1024);
  const tb = bytes / (1024 * 1024 * 1024 * 1024);

  if (tb >= 1) {
    return `${tb.toFixed(2)} TB`;
  } else if (gb >= 1) {
    return `${gb.toFixed(2)} GB`;
  } else if (mb >= 1) {
    return `${mb.toFixed(2)} MB`;
  } else if (kb >= 1) {
    return `${kb.toFixed(2)} KB`;
  } else {
    return `${bytes} B`;
  }
}

/**
 * Convert bytes to GB as a number (for calculations)
 * @param bytes Storage size in bytes
 * @returns Storage size in GB
 */
export function bytesToGB(bytes: number | undefined | null): number {
  if (bytes === undefined || bytes === null) {
    return 0;
  }

  return bytes / (1024 * 1024 * 1024);
}

/**
 * Convert bytes to MB as a number (for calculations)
 * @param bytes Storage size in bytes
 * @returns Storage size in MB
 */
export function bytesToMB(bytes: number | undefined | null): number {
  if (bytes === undefined || bytes === null) {
    return 0;
  }

  return bytes / (1024 * 1024);
}
